import { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Dataroom } from '@vcip/design-system';
import { useSessionStore } from '@/store/sessionStore';
import { bootstrapPlan } from './flow/planBootstrap';
import { decideEntry, mayEnterCall } from './flow/preCallGate';
import {
  loadSessionByInvite,
  resumeSession,
  setSessionToken,
  exchangeSessionToken,
} from '@/api';
import { FlowShell } from '@/flow/FlowShell';
import { PoweredByKycHub } from '@/flow/components/PoweredByKycHub';
import { SessionLanding } from '@/flow/SessionLanding';
import { LocaleProvider } from '@/i18n/runtime';

/**
 * Routing separates "which session" (URL) from "which step" (the step machine
 * in the store):
 *   /session/:sessionId — the invite link's front door: the customer entry
 *                         landing (state-aware; CTA → /start or /call).
 *   /start/:sessionId  — S7-6 pre-session flow entry: consent → self-capture →
 *                        waiting → call, for a session still being prepared.
 *   /s/:sessionToken   — enter via magic-link invite; hydrate + run the flow.
 *   /resume/:token     — the reconnection fix: rejoin a SUSPENDED session by its
 *                        time-boxed resume token (same id, steps, verifier).
 */
export function App(): JSX.Element {
  // VC-12: Arabic is offered only when the session's pack names it — absent on every India session.
  const packLocales = useSessionStore((s) => s.packLocales);
  return (
    <LocaleProvider packLocales={packLocales}>
      <BrowserRouter>
      <Routes>
        <Route path="/" element={<InviteRequired />} />
        <Route path="/session/:sessionId" element={<SessionLanding />} />
        <Route path="/start/:sessionId" element={<StartRoute />} />
        <Route path="/s/:sessionToken" element={<FlowRoute />} />
        <Route path="/resume/:resumeToken" element={<ResumeRoute />} />
        <Route path="/call/:sessionId" element={<CallRoute />} />
        <Route path="/idrail/callback" element={<RailCallbackRoute />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </LocaleProvider>
  );
}

/** Enter the flow from an invite token; hydrate the session then render it. */
function FlowRoute(): JSX.Element {
  const { sessionToken } = useParams();
  const initSession = useSessionStore((s) => s.initSession);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    // Exchange the link's session id for a SIGNED, EXPIRING, session-scoped bearer, once, here.
    // The id itself stops being a credential at this point: everything after carries a token that
    // is signed, dies on its own, and is revoked the moment the session ends. Kept in memory and
    // sent as a header — never left in the URL or logs (DATAROOM_AUDIT S2).
    if (sessionToken) {
      void exchangeSessionToken(sessionToken).then((token) => {
        if (!active) return;
        // A definitive refusal (unknown/finished/dead session) means no call after this one can
        // succeed — the raw id stopped being a credential when the exchange shipped. Route to the
        // state-aware landing, which tells the customer honestly what state their session is in,
        // instead of mounting a flow whose every request will silently fail.
        if (token === null) {
          navigate(`/session/${encodeURIComponent(sessionToken)}`, { replace: true });
          return;
        }
        // 18-Aug RCA (owner item #2): THIS route never read the frozen plan, so the hardcoded
        // legacy flow ran and a REQUIRED DigiLocker rendered as skippable. Read it here, AFTER
        // the bearer exists (chained, not raced), exactly as /start and /call do.
        void bootstrapPlan(sessionToken, () => active);
      });
    } else {
      setSessionToken(null);
    }

    // KYCID·00001-00003-00001 (AC1, belt): a customer who dropped mid-call and
    // reloaded /s/:token must REJOIN, not restart the flow. The invite-entry read is
    // mock/best-effort, so we do not wait on it: if the persisted session is already
    // in the call band (or holds a resume token), route straight to /call/:id, whose
    // CallRoute re-fetches a grant and rejoins the persistent room (the BE then drives
    // RECONNECTING/SUSPENDED→RESUMED→LIVE). This closes the observed dead end where a
    // reconnecting customer landed on a stale consent step with a failing notice load.
    const p0 = useSessionStore.getState().session;
    const inCallBand = p0.currentStep === 'call' || p0.currentStep === 'waiting';
    if (p0.sessionId && (inCallBand || p0.resumeToken)) {
      navigate(`/call/${p0.sessionId}`, { replace: true });
      return () => {
        active = false;
      };
    }

    void loadSessionByInvite(sessionToken ?? 'missing')
      .then((cfg) => {
        if (!active) return;
        const persisted = useSessionStore.getState().session;
        // If sessionStorage restored an in-progress flow (a reload mid-journey),
        // preserve it — resume in place, do NOT restart at welcome.
        const midFlow = persisted.currentStep !== 'welcome';
        // KYCID·00001-00003-00001 (AC1): the SERVER's FSM is authoritative for the call
        // band. A customer who dropped mid-call and reloaded must REJOIN the live call —
        // never be stranded on a stale persisted pre-call step (the observed consent-load
        // dead end). So a server-reported call-band step wins over the persisted step.
        const serverInCallBand = cfg.currentStep === 'call' || cfg.currentStep === 'waiting';
        initSession({
          sessionId: cfg.sessionId,
          tenantId: cfg.tenantId,
          regime: cfg.regime,
          reference: cfg.reference,
          customerName: cfg.customerName,
          agentId: cfg.agentId,
          agentName: cfg.agentName,
          resumeToken: cfg.resumeToken ?? persisted.resumeToken,
          ...(midFlow && !serverInCallBand
            ? {}
            : { currentStep: cfg.currentStep, completedSteps: cfg.completedSteps }),
        });
      })
      .catch(() => {
        /* skeleton: keep the initial mock session on failure */
      });

    return () => {
      active = false;
    };
  }, [sessionToken, initSession, navigate]);

  return <FlowShell />;
}

/** Resume a suspended session from its resume token, then continue the flow. */
function ResumeRoute(): JSX.Element {
  const { resumeToken } = useParams();
  const initSession = useSessionStore((s) => s.initSession);
  const resumeFromToken = useSessionStore((s) => s.resumeFromToken);
  const navigate = useNavigate();

  useEffect(() => {
    if (!resumeToken) return;
    void resumeSession(resumeToken)
      .then((cfg) => {
        // Apply ONLY the server-authoritative fields. The resume-critical slice (completedSteps, reference,
        // customerName, verifier affinity) is preserved from the persisted store — a resume is the SAME session,
        // never a restart (PRD-SE-11/S7-8); the capability read does not re-disclose that PII.
        initSession({
          sessionId: cfg.sessionId,
          tenantId: cfg.tenantId,
          regime: cfg.regime,
          currentStep: cfg.currentStep,
        });
        resumeFromToken(resumeToken); // same id, steps, verifier affinity retained
        // Re-join the SAME persistent live room: the widened join guard now mints a token for the reconnection
        // band, so /call/:sessionId re-fetches a grant and continues the one continuous recording.
        navigate(`/call/${cfg.sessionId}`, { replace: true });
      })
      // Non-resumable / not-found → the state-aware landing renders an honest "session ended" (token = id here).
      .catch(() => navigate(`/session/${resumeToken}`, { replace: true }));
  }, [resumeToken, initSession, resumeFromToken, navigate]);

  return <FlowShell />;
}

/**
 * Enter the pre-session flow (S7-6) for a session the landing reported as
 * still being prepared: consent ceremony → document/selfie self-capture →
 * queue:enter → waiting room → call. The consent and capture STEPS are the
 * point — this is the path on which the warm-up scores REAL customer evidence.
 *
 * Resume honesty: if sessionStorage holds an in-progress flow for the SAME
 * session, we resume at its persisted step (a reload is never a restart);
 * a different (or absent) session id starts this session's flow at consent.
 * The capture step re-syncs against the server artefact list on mount either
 * way — the client's persisted position is a hint, the server is the truth.
 */
function StartRoute(): JSX.Element {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const initSession = useSessionStore((s) => s.initSession);
  const goToStep = useSessionStore((s) => s.goToStep);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    const persisted = useSessionStore.getState().session;
    const sameSessionMidFlow =
      persisted.sessionId === sessionId && persisted.currentStep !== 'welcome';
    initSession({ sessionId });

    // 16-Aug P0 belt: seed the bearer on ANY entry into the flow — links, reloads and the
    // landing's Start all pass through here, and an unexchanged entry 401s every call after it.
    // 18-Aug RCA: the plan read is CHAINED after the exchange (it used to race it and 401).
    //
    // KYCID-FSD-00013 §8 — pin the frozen check plan ONCE from the server's authority so the flow
    // order is plan-derived. A plan read that fails is reported and marks the plan 'unknown' —
    // the shell blocks with a retry; the hardcoded FLOW_ORDER is never run against a real backend.
    void exchangeSessionToken(sessionId).then((token) => {
      if (!active) return;
      if (token === null) {
        navigate(`/session/${encodeURIComponent(sessionId)}`, { replace: true });
        return;
      }
      void bootstrapPlan(sessionId, () => active);
    });

    // Fresh entry lands on the SCHEDULE fork (now-vs-later — SPEC-07 §9), the flow's
    // first post-welcome step; consent follows it (and the pre-call checks follow consent).
    if (!sameSessionMidFlow) goToStep('schedule');
    return () => {
      active = false;
    };
  }, [sessionId, initSession, goToStep, navigate]);

  return <FlowShell />;
}

/**
 * Direct-entry a given session straight into the live call as CUSTOMER
 * (/call/:sessionId). A convenience entry alongside the full invite flow: it
 * hydrates the session id and jumps the flow machine to the `call` step, so the
 * same LiveCall screen (real two-way LiveKit video) renders for that session.
 */
function CallRoute(): JSX.Element {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const initSession = useSessionStore((s) => s.initSession);
  const goToStep = useSessionStore((s) => s.goToStep);

  // `/call/:id` is a shareable URL and was the bypass: it entered the video leg without ever reading the
  // frozen plan, so every REQUIRED pre-call check was skipped. It now consults the plan first and sends the
  // customer back to the pre-call rail if anything is owed. Fail-closed — an unreadable or empty plan also
  // routes to /start, which shows an honest retry rather than opening the call on a guess.
  //
  // This is a belt to the server's braces (CheckJoinGate is the authority); the front door must simply stop
  // being the thing that opens the bypass.
  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    // 16-Aug P0 belt (same as StartRoute): /call/:id is shareable — seed the bearer before the
    // plan consult; without it the plan read itself 401s and the honest-retry loop never ends.
    // 18-Aug RCA: chained after the exchange (not raced); a refused exchange routes to the landing.
    void exchangeSessionToken(sessionId).then(async (token) => {
      if (!active) return;
      if (token === null) {
        navigate(`/session/${encodeURIComponent(sessionId)}`, { replace: true });
        return;
      }
      const dto = await bootstrapPlan(sessionId, () => active);
      if (!active) return;
      if (dto !== null && mayEnterCall(decideEntry(dto, false))) {
        initSession({ sessionId, status: 'live' });
        goToStep('call');
      } else {
        navigate(`/start/${sessionId}`, { replace: true });
      }
    });
    return () => {
      active = false;
    };
  }, [sessionId, initSession, goToStep, navigate]);

  return <FlowShell />;
}

/**
 * The DigiLocker/Digitap identity-rail RETURN leg (KYCID·00001-00005-00009).
 *
 * {@code IdentityRailProperties.redirectPath = "/idrail/callback"} is a FIXED, session-AGNOSTIC registered
 * redirect_uri: the provider cannot echo a per-session token, so this bridge recovers the session from the
 * same-tab pending record we wrote before leaving (Aadhaar.begin — {@code 'vcip.digilocker.pending'}),
 * re-seeds the interim capability token, and hands off to the invite-entry route {@code /s/:id}, which
 * REJOINS at the persisted step (midFlow preserved). The Aadhaar return-leg effect then relays OUR saved
 * state to the be and shows "verified ✓".
 *
 * WHY /s and not /start: {@code /s} re-seeds {@code setSessionToken} (the cross-origin round trip wiped the
 * in-memory bearer) AND rejoins in place; {@code /start} does neither safely. We seed the token HERE, BEFORE
 * navigate, because child (Aadhaar) effects run before the parent (/s) route effect — otherwise the relay
 * fires unauthenticated (401) and the verification is lost. We PRESERVE the query string so Aadhaar's
 * URL-`state` fallback still applies, and we do NOT clear the pending record — the Aadhaar return leg is its
 * sole owner. Pending absent/unparseable (different tab, cleared storage, stale bookmark) ⇒ honest NotFound,
 * never a fabricated success, never a hanging spinner.
 *
 * @implements KYCID·00001-00005-00009
 */
function RailCallbackRoute(): JSX.Element {
  const navigate = useNavigate();
  const [recoverable, setRecoverable] = useState(true);

  useEffect(() => {
    let sessionId: string | null = null;
    try {
      const raw = window.sessionStorage.getItem('vcip.digilocker.pending');
      if (raw) {
        const parsed = JSON.parse(raw) as { sessionId?: string };
        if (parsed && typeof parsed.sessionId === 'string' && parsed.sessionId) {
          sessionId = parsed.sessionId;
        }
      }
    } catch {
      /* unreadable/unparseable — fall through to the honest not-found */
    }
    if (!sessionId) {
      setRecoverable(false);
      return;
    }
    // Re-exchange BEFORE handoff: the cross-origin round trip cleared the in-memory bearer, and
    // Aadhaar's (child) relay effect runs before FlowRoute's (parent) seeding. The exchange is
    // idempotent and concurrent callers share one in-flight request, so the double-fire is safe.
    void exchangeSessionToken(sessionId);
    // Preserve the provider query (state/txnId/success) so the URL-state fallback still applies; the
    // sessionStorage pending state stays PRIMARY. Do NOT remove the pending key — the Aadhaar return leg
    // claims and clears it.
    navigate(`/s/${encodeURIComponent(sessionId)}${window.location.search}`, { replace: true });
  }, [navigate]);

  if (!recoverable) return <NotFound />;
  return (
    <Dataroom>
      <main className="app-screen">
        <div className="app-view center">
          <div className="center-col">
            <div className="app-preparing" role="status">
              Finishing your verification…
            </div>
          </div>
        </div>
        <PoweredByKycHub />
      </main>
    </Dataroom>
  );
}

/**
 * The bare-domain landing (KYCID·00001-00003-00003). "/" carries no session — the
 * link IS the credential — so instead of stranding a real customer on a
 * placeholder screen, direct them to their invite link (SMS/email). Honest, no
 * fabricated session.
 *
 * @implements KYCID·00001-00003-00003
 */
function InviteRequired(): JSX.Element {
  return (
    <Dataroom>
      <main className="app-screen">
        <div className="app-view center">
          <div className="center-col">
            <div className="h-title sm">Open your invite link</div>
            <p className="sub" style={{ maxWidth: '32ch' }}>
              To start or rejoin your video-KYC, open the secure link we sent you by
              SMS or email. If you were already in a call, reopen that same link.
            </p>
          </div>
        </div>
        <PoweredByKycHub />
      </main>
    </Dataroom>
  );
}

function NotFound(): JSX.Element {
  return (
    <Dataroom>
      <main className="app-screen">
        <div className="app-view center">
          <div className="center-col">
            <div className="h-title sm">This link isn't valid</div>
            <p className="sub" style={{ maxWidth: '30ch' }}>
              Your verification link may have expired. Please use the latest link
              from your email or SMS.
            </p>
          </div>
        </div>
        <PoweredByKycHub />
      </main>
    </Dataroom>
  );
}
